import {
  buildUrlPrefix,
  getCategoryRoute as getCoreCategoryRoute,
  getProductRoute as getCoreProductRoute,
} from '@shopware/helpers'
import { useUrlResolver } from '#imports'
import type { Schemas } from '#shopware'

// Module-level so we warn only ONCE per broken entity (per server instance /
// page load) instead of on every re-render.
const warnedBrokenRoutes = new Set<string>()

export const useShopRouter = () => {
  const localePath = useLocalePath()
  const urlResolver = useUrlResolver()
  const urlPrefix = urlResolver.getUrlPrefix()

  // Guard against broken links: the core helpers can return a path containing
  // the literal string "undefined" (e.g. a link category whose target is not
  // resolvable, or a category without a seoUrl falling back to
  // `/${getEntityPrefix()}/${id}` where the prefix is undefined). Such links
  // get crawled and time out (JPR-646), so we suppress them instead. The check
  // must run on the FINAL composed path — getCoreCategoryRoute/-ProductRoute
  // return an object ({ path, state }), not a string.
  //
  // After the seoUrls fix (server/api/shop-navigation) this should essentially
  // never fire. If it does, a category is misconfigured or the nav seoUrls
  // association broke again — so we don't just swallow it: we log a warning
  // (deduped) with enough context to find the culprit. The `[nav][broken-route]`
  // prefix is meant for a log-based alert.
  const buildRoute = (
    route: ReturnType<typeof getCoreCategoryRoute>,
    entity: Schemas['Category'] | Schemas['Product'],
    kind: 'category' | 'product'
  ): string | undefined => {
    const finalPath = buildUrlPrefix(route, urlPrefix).path
    if (typeof finalPath !== 'string' || finalPath.includes('undefined')) {
      const e = entity as Record<string, unknown>
      // A deactivated or nav-hidden category legitimately has no route: Shopware
      // does not generate a seoUrl for it, so the path falls back to the
      // "undefined" form. That's expected, not a misconfiguration, so we drop the
      // link silently. Only an active AND visible entity with a broken path
      // signals a real problem worth alerting on. `=== false` (not falsy) so that
      // entities where the flag is simply not loaded still warn as before.
      const intentionallyHidden = e?.active === false || e?.visible === false
      const key = `${kind}:${(e?.id as string) ?? finalPath}`
      if (!intentionallyHidden && !warnedBrokenRoutes.has(key)) {
        warnedBrokenRoutes.add(key)
        console.warn('[nav][broken-route] suppressed undefined link', {
          kind,
          id: e?.id,
          name:
            (e?.translated as Record<string, unknown> | undefined)?.name ??
            e?.name,
          type: e?.type,
          active: e?.active,
          visible: e?.visible,
          linkType: e?.linkType,
          internalLink: e?.internalLink,
          finalPath,
        })
      }
      return undefined
    }
    return localePath(finalPath)
  }

  const getCategoryRoute = (category: Schemas['Category']) =>
    buildRoute(getCoreCategoryRoute(category), category, 'category')

  const getProductRoute = (product: Schemas['Product']) =>
    buildRoute(getCoreProductRoute(product), product, 'product')

  const getShopRoute = (route: string) => {
    return localePath(buildUrlPrefix(route, urlPrefix).path)
  }

  return {
    getCategoryRoute,
    getProductRoute,
    getShopRoute,
    urlPrefix,
  }
}
